import { setTargetBehaviorInputSchema } from '@cc/schema';
import { useTranslate } from '@refinedev/core';
import {
  App as AntdApp,
  Checkbox,
  Form,
  Grid,
  Input,
  InputNumber,
  Radio,
  Space,
  Typography,
} from 'antd';
import { useEffect, useState } from 'react';
import { SectionDrawer } from '../../components/ToolSection';
import {
  FA_FUNCTIONS,
  FA_MEASURES,
  emptyDraft,
  setInputOf,
  toggleFaFunction,
  type FaFunction,
  type FaMeasure,
  type SummaryRow,
  type TargetDraft,
} from './model';
import { useFaCommand } from './useFaCommand';

/**
 * E48 · SetTargetBehavior — the Behavior drawer (Set from the header / the empty line, Edit from the behavior's own card). The fields
 * stand on antd's Form label/field grid (N-7 (1): the "Measure" label no longer collides with the radios); below 600 px the labels
 * sit above the fields. Save validates; a problem or a refusal is a lasting sentence above the footer buttons. The function is a set of
 * checkboxes (Q-e48f-1 A, 2026-10-24): one or more of the four, or "not sure yet" alone.
 */
type Tx = (key: string, options?: Record<string, unknown>) => string;

const draftOf = (existing: SummaryRow | null): TargetDraft =>
  existing
    ? {
        name: existing.target.name,
        description: existing.target.description ?? '',
        measure: existing.target.measure as FaMeasure,
        functionHypotheses: existing.target.functionHypotheses as FaFunction[],
        baselineWeeks: existing.target.baselineWeeks,
      }
    : emptyDraft();

type Props = {
  open: boolean;
  clientId: string;
  /** The behavior being edited; null = a new one. */
  existing: SummaryRow | null;
  onClose: () => void;
  /** The saved behavior's id (null when the answer carried none). */
  onSaved: (id: string | null) => void;
};

export const FaBehaviorDrawer = ({
  open,
  clientId,
  existing,
  onClose,
  onSaved,
}: Props) => {
  const t = useTranslate() as unknown as Tx;
  const { message } = AntdApp.useApp();
  const screens = Grid.useBreakpoint();
  const { run, isLoading } = useFaCommand();
  const [opening, setOpening] = useState<TargetDraft>(emptyDraft);
  const [draft, setDraft] = useState<TargetDraft>(emptyDraft);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const d = draftOf(existing);
    setOpening(d);
    setDraft(d);
    setError(null);
    // a fresh form each time it opens — it is a new decision
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const edit = (patch: Partial<TargetDraft>) => {
    setDraft((d) => ({ ...d, ...patch }));
    setError(null);
  };

  const onSave = async () => {
    if (!draft.name.trim()) {
      setError(t('fa.target.nameRequired'));
      return;
    }
    setError(null);
    const input = {
      ...setInputOf(clientId, draft),
      ...(existing ? { targetId: existing.targetId } : {}),
    };
    // both ends: the very codec the API's shield runs (a malformed choice never leaves the drawer)
    try {
      await setTargetBehaviorInputSchema.validate(input);
    } catch {
      setError(t('fa.errors.FA_VALUE_INVALID'));
      return;
    }
    const res = await run(
      'setTargetBehavior',
      input,
      t('fa.errors.FA_VALUE_INVALID'),
    );
    if (res.ok) {
      message.success(t('fa.target.saved'));
      onSaved(res.id);
    } else {
      setError(res.message);
    }
  };

  const phone = screens.sm === false;

  return (
    <SectionDrawer
      open={open}
      title={t('fa.target.title')}
      testId="fa-set-drawer"
      saveTestId="fa-set-save"
      context={
        <Typography.Text type="secondary">{t('fa.target.intro')}</Typography.Text>
      }
      saveLabel={t('fa.target.save')}
      saving={isLoading}
      error={error}
      dirty={JSON.stringify(draft) !== JSON.stringify(opening)}
      onSave={() => {
        onSave().catch(() => undefined);
      }}
      onClose={onClose}
    >
      <Form
        layout={phone ? 'vertical' : 'horizontal'}
        labelCol={phone ? undefined : { flex: '170px' }}
        // '1 1 0%', not 1: antd turns a number into `1 1 auto`, and long radio lines then push the field below its label
        wrapperCol={phone ? undefined : { flex: '1 1 0%', style: { minWidth: 0 } }}
        labelWrap
        colon={false}
      >
        <Form.Item label={t('fa.target.name')}>
          <Input
            data-testid="fa-set-name"
            maxLength={200}
            onChange={(e) => edit({ name: e.target.value })}
            value={draft.name}
          />
        </Form.Item>
        <Form.Item label={t('fa.target.description')}>
          <Input.TextArea
            autoSize={{ minRows: 2, maxRows: 4 }}
            data-testid="fa-set-description"
            maxLength={1000}
            onChange={(e) => edit({ description: e.target.value })}
            value={draft.description}
          />
        </Form.Item>
        <Form.Item label={t('fa.target.measure')}>
          <Radio.Group
            data-testid="fa-set-measure"
            onChange={(e) => edit({ measure: e.target.value as FaMeasure })}
            value={draft.measure}
          >
            <Space direction="vertical">
              {FA_MEASURES.map((m) => (
                <Radio key={m} value={m} data-testid={`fa-set-measure-${m}`}>
                  {t(`fa.measure.${m}`)}
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        </Form.Item>
        <Form.Item label={t('fa.target.baseline')}>
          <InputNumber
            data-testid="fa-set-baseline"
            min={0}
            max={52}
            onChange={(v) => edit({ baselineWeeks: Number(v ?? 0) })}
            value={draft.baselineWeeks}
          />
        </Form.Item>
        <Form.Item
          label={t('fa.target.function')}
          // Q-e48f-1 A (2026-10-24): one or more functions; "not sure yet" only alone (toggleFaFunction keeps the rule)
          extra={
            <span data-testid="fa-set-function-hint">{t('fa.functionHint')}</span>
          }
        >
          <div data-testid="fa-set-function" role="group">
            <Space direction="vertical">
              {FA_FUNCTIONS.map((f) => (
                <Checkbox
                  key={f}
                  checked={draft.functionHypotheses.includes(f)}
                  data-testid={`fa-set-function-${f}`}
                  onChange={() =>
                    edit({
                      functionHypotheses: toggleFaFunction(
                        draft.functionHypotheses,
                        f,
                      ),
                    })
                  }
                >
                  <strong>{t(`fa.function.${f}`)}</strong>
                  <Typography.Text type="secondary" style={{ display: 'block' }}>
                    {t(`fa.functionLine.${f}`)}
                  </Typography.Text>
                </Checkbox>
              ))}
            </Space>
          </div>
        </Form.Item>
      </Form>
    </SectionDrawer>
  );
};
